import React from 'react';
import { PAGINATION_CONFIG, getPageInfo, getPageNumbers } from '../../config/pagination';
import { 
  RiArrowLeftSLine, 
  RiArrowRightSLine, 
  RiArrowLeftDoubleLine, 
  RiArrowRightDoubleLine 
} from 'react-icons/ri';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
  showSummary?: boolean;
  className?: string;
  /** Legacy prop: ignored (totalPages is computed from totalItems/pageSize) */
  totalPages?: number;
  /** Legacy prop alias for PAGINATION_CONFIG.PAGE_SIZE_OPTIONS */
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  pageSize = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  showSummary = PAGINATION_CONFIG.SHOW_SUMMARY,
  className = ''
}) => {
  const pageInfo = getPageInfo(currentPage, pageSize, totalItems);
  const pageNumbers = getPageNumbers(currentPage, pageInfo.totalPages);
  const { LABELS } = PAGINATION_CONFIG;
  
  if (totalItems === 0) {
    return (
      <div className={`flex items-center justify-center py-4 text-secondary-500 ${className}`}>
        {LABELS.NO_DATA}
      </div>
    );
  }
  
  if (pageInfo.totalPages <= 1 && !showPageSizeSelector) {
    return null;
  }
  
  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 py-3 ${className}`}>
      {/* Summary */}
      {showSummary && (
        <div className="text-xs text-secondary-600" role="status" aria-live="polite" aria-atomic="true">
          แสดง <span className="font-medium text-secondary-700">{pageInfo.showingFrom}–{pageInfo.showingTo}</span> จากทั้งหมด <span className="font-medium text-secondary-700">{totalItems}</span> รายการ
        </div>
      )}

      {/* BUG-S2173-001: ที่ 375px แถวนี้ (ตัวเลือกจำนวนแถว + ปุ่ม 44px × ~11) กว้าง ~440px ดัน main ให้เลื่อน
          ซ้าย-ขวาได้ ทุกหน้าที่ใช้ Pagination · ให้ตัดบรรทัดได้ และบนมือถือโชว์เฉพาะเลขหน้าปัจจุบัน */}
      <div className="flex flex-wrap items-center justify-center gap-3 max-w-full">
        {/* Page size selector */}
        {showPageSizeSelector && onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-secondary-500">{LABELS.ROWS_PER_PAGE}:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="จำนวนแถวต่อหน้า"
              className="px-2 h-11 sm:h-7 border border-secondary-200 rounded-md text-xs text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 bg-white min-w-[44px]"
            >
              {PAGINATION_CONFIG.PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        )}

        {/* Pagination controls */}
        <div className="flex items-center gap-0.5">
          {/* First page */}
          {PAGINATION_CONFIG.SHOW_FIRST_LAST && (
            <button
              onClick={() => onPageChange(1)}
              disabled={!pageInfo.hasPreviousPage}
              className="flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title={LABELS.FIRST}
            >
              <RiArrowLeftDoubleLine className="w-4 h-4" />
            </button>
          )}

          {/* Previous page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!pageInfo.hasPreviousPage}
            className="flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title={LABELS.PREVIOUS}
          >
            <RiArrowLeftSLine className="w-4 h-4" />
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-0.5">
            {pageNumbers[0] > 1 && (
              <>
                <button
                  onClick={() => onPageChange(1)}
                  className="hidden sm:flex items-center justify-center min-w-[44px] h-11 sm:min-w-[32px] sm:h-8 px-2 rounded-md text-xs text-secondary-600 hover:bg-secondary-100 transition-colors"
                >
                  1
                </button>
                {pageNumbers[0] > 2 && (
                  <span className="hidden sm:flex items-center justify-center w-8 h-8 text-xs text-secondary-600">···</span>
                )}
              </>
            )}

            {pageNumbers.map(page => (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                aria-current={page === currentPage ? 'page' : undefined}
                className={`items-center justify-center min-w-[44px] h-11 sm:min-w-[32px] sm:h-8 px-2 rounded-md text-xs font-medium transition-colors ${
                  page === currentPage
                    ? 'flex bg-primary-600 text-white shadow-sm'
                    : 'hidden sm:flex text-secondary-600 hover:bg-secondary-100'
                }`}
              >
                {page}
              </button>
            ))}

            {pageNumbers[pageNumbers.length - 1] < pageInfo.totalPages && (
              <>
                {pageNumbers[pageNumbers.length - 1] < pageInfo.totalPages - 1 && (
                  <span className="hidden sm:flex items-center justify-center w-8 h-8 text-xs text-secondary-600">···</span>
                )}
                <button
                  onClick={() => onPageChange(pageInfo.totalPages)}
                  className="hidden sm:flex items-center justify-center min-w-[44px] h-11 sm:min-w-[32px] sm:h-8 px-2 rounded-md text-xs text-secondary-600 hover:bg-secondary-100 transition-colors"
                >
                  {pageInfo.totalPages}
                </button>
              </>
            )}
          </div>

          {/* Next page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!pageInfo.hasNextPage}
            className="flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title={LABELS.NEXT}
          >
            <RiArrowRightSLine className="w-4 h-4" />
          </button>

          {/* Last page */}
          {PAGINATION_CONFIG.SHOW_FIRST_LAST && (
            <button
              onClick={() => onPageChange(pageInfo.totalPages)}
              disabled={!pageInfo.hasNextPage}
              className="flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 rounded-md text-secondary-600 hover:bg-secondary-100 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title={LABELS.LAST}
            >
              <RiArrowRightDoubleLine className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;